import { useCallback, useEffect, useMemo, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { fetchNudgeThrottlingState } from '../utils/fetchNudgeThrottlingState';

type UseFetchNudgeThrottlingStateProps = {
	hasNudgeToShow?: boolean;
	triggerId: string;
};

type UseFetchNudgeThrottlingStateReturnType = {
	error?: Error;
	isLoading: boolean;
	isNudgeThrottled: boolean;
};

export const useFetchNudgeThrottlingState = ({
	triggerId,
	hasNudgeToShow,
}: UseFetchNudgeThrottlingStateProps): UseFetchNudgeThrottlingStateReturnType => {
	const [isNudgeThrottled, setIsNudgeThrottled] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<Error | undefined>(undefined);

	const makeNudgeThrottlingStateRequest = useCallback(async () => {
		try {
			const nudgeThrottlingState = await fetchNudgeThrottlingState({ triggerId });
			setIsNudgeThrottled(
				nudgeThrottlingState.triggers[0].recommendations[0].recommendation_details.nudge_throttled,
			);
		} catch (error) {
			setError(error as Error);
		} finally {
			setIsLoading(false);
		}
	}, [triggerId]);

	useEffect(() => {
		if (!hasNudgeToShow) {
			return;
		}

		// eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-registration
		const isNudgeThrottlingEnabled = fg('rovo_button_nudge_throttling_enabled');
		const isNudgeRecommendationMigrationEnabled = fg('aix_nudge_recommendation_migration_enabled');
		if (isNudgeThrottlingEnabled && !isNudgeRecommendationMigrationEnabled) {
			void makeNudgeThrottlingStateRequest();
			return;
		}

		setIsLoading(false);
	}, [makeNudgeThrottlingStateRequest, hasNudgeToShow]);

	return useMemo(() => {
		const getNudgeThrottlingState = () => {
			if (error || !isNudgeThrottled) {
				return false;
			}
			return isNudgeThrottled;
		};

		return {
			isNudgeThrottled: getNudgeThrottlingState(),
			isLoading,
			error,
		};
	}, [isNudgeThrottled, isLoading, error]);
};
